// src/pages/Users.js
import { useEffect, useState } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../api";
import { Button } from "../../../components/ui/Button";
import { Search, ChevronLeft, ChevronRight, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { createPortal } from "react-dom";


export default function Users() {
    const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [menuOpen, setMenuOpen] = useState(null); // store which row menu is open
  const navigate = useNavigate();
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const res = await api.fetchUsers({ page: currentPage, limit: 20, search: searchQuery });
        setUsers(res.data.data || []);
        setTotalPages(res.data.pages || 1);
      } catch (err) {
        setError(err.response?.data?.error || err.message || "Failed to load users");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [currentPage, searchQuery]);

  const loadUsers = async () => {
    try {
      const res = await api.fetchUsers({ page: currentPage, limit: 20, search: searchQuery });
      setUsers(res.data.data || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      setError("Failed to load users: " + err.message);
    } finally {
      setLoading(false);
    }
  };

const handleMenuToggle = (event, userId) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + window.scrollY,
      left: rect.right + window.scrollX - 160,
    });
    setMenuOpen(menuOpen === userId ? null : userId);
  };

  // Find the selected user to pass to the handlers
  const selectedUser = users.find((s) => s.id === menuOpen);

  const handleEdit = (user) => {
    setMenuOpen(null);
    navigate("/user-edit/", { state: user });
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await api.deleteUser(userId);
      loadUsers();
    } catch (err) {
      alert("Failed to delete user: " + err.message);
    }
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-secondary-900">Users</h1>
        <Link to="/register" className="w-full sm:w-auto">
          <button className="w-full sm:w-auto px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-semibold transition-colors">
            + New User
          </button>
        </Link>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white shadow-soft rounded-xl overflow-x-auto border border-secondary-100">
        <div className="p-4">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-secondary-500 font-medium">Loading users...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">{error}</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-secondary-500 font-medium">No users found.</p>
            </div>
          ) : (
            <div>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-secondary-200">
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">ID</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Name</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Email</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Phone</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Role</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Status</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider text-right pr-6">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, index) => (
                    <tr key={u.id} className="border-b border-secondary-100 hover:bg-secondary-50 transition-colors">
                      <td className="py-3 px-4 text-sm text-secondary-900 font-medium">#{(currentPage - 1) * 20 + index + 1}</td>
                      <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{u.name}</td>
                      <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{u.email}</td>
                      <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{u.phone_number}</td>
                      <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{u.role_name || u.role_id}</td>
                      <td className="py-3 px-4 text-sm text-secondary-900 font-medium">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {u.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-right pr-4 relative">
                        <button
                          className="p-1.5 rounded-lg hover:bg-secondary-100 text-secondary-600 hover:text-secondary-900 transition-colors cursor-pointer"
                          onClick={(e) => handleMenuToggle(e, u.id)}
                          title="Actions"
                        >
                          <MoreVertical size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              
            </table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-secondary-200 bg-gray-50">
              <div className="text-sm text-secondary-500 font-medium">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-white border border-secondary-200 text-secondary-700 hover:bg-secondary-50"
                >
                  <ChevronLeft size={16} />
                </Button>
                <Button 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-white border border-secondary-200 text-secondary-700 hover:bg-secondary-50"
                >
                  <ChevronRight size={16} />
                </Button>
            </div>
            </div>
              {/* The portal for the context menu */}
              {menuOpen &&
                selectedUser &&
                createPortal(
                  <div
                    className="fixed bg-white border border-secondary-200 rounded-xl shadow-xl z-50 min-w-[160px] py-1.5 animate-in fade-in zoom-in-95 duration-100"
                    style={{ top: menuPosition.top, left: menuPosition.left }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="flex items-center gap-2 w-full px-4 py-2 text-left hover:bg-secondary-50 text-sm font-medium text-secondary-800 transition-colors cursor-pointer"
                      onClick={() => handleEdit(selectedUser)}
                    >
                      <Pencil size={14} /> Edit
                    </button>
                    <button
                      className="flex items-center gap-2 w-full px-4 py-2 text-left hover:bg-red-50 text-red-600 text-sm font-medium transition-colors cursor-pointer"
                      onClick={() => handleDelete(selectedUser.id)}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>,
                  document.body
                )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4 mt-4">
        {loading ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-secondary-500 font-medium">Loading users...</p>
          </div>
        ) : error ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-red-500">{error}</p>
          </div>
        ) : users.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-secondary-500 font-medium">No users found.</p>
          </div>
        ) : (
          users.map((u, index) => (
            <div
              key={u.id}
              className="bg-white shadow rounded-lg p-4 border border-gray-200"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b pb-2">
                  <span className="text-xs text-gray-500 font-medium">ID</span>
                  <span className="text-sm font-semibold text-gray-900">#{(currentPage - 1) * 20 + index + 1}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Name</span>
                  <span className="text-sm text-gray-900 font-medium">{u.name}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Email</span>
                  <span className="text-sm text-gray-900 break-all">{u.email}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Phone</span>
                  <span className="text-sm text-gray-900">{u.phone_number}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Role</span>
                  <span className="text-sm text-gray-900">{u.role_name || u.role_id}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Status</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {u.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                
                <div className="pt-2 border-t space-y-2">
                  <button
                    onClick={() => handleEdit(u)}
                    className="flex items-center justify-center gap-1.5 w-full text-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    <Pencil size={14} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(u.id)}
                    className="flex items-center justify-center gap-1.5 w-full text-center bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors font-medium"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Click outside to close menu */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setMenuOpen(null)}
        />
      )}
    </DashboardLayout >
  );
}
